// src/admin/pages/AdminOrderDetails.js
import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
  FaArrowLeft,
  FaTruck,
  FaMoneyBill,
  FaUser,
  FaBox,
} from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminOrderById,
  updateAdminOrderStatus,
  updateAdminOrderCustomer,
} from "../../services/adminOrders.api";
import { buildOrderConfirmationWhatsAppMessage } from "../../utils/whatsappOrderMessages";
import { buildReviewRequestWhatsAppMessage } from "../../utils/whatsappOrderReviewMessages";

function AdminOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [editingCustomer, setEditingCustomer] = useState(false);
const [customerDraft, setCustomerDraft] = useState(null);
const [showCustomerToast, setShowCustomerToast] = useState(false);

  const TEXT = {
    EN: {
      back: "Back to Orders",
      order: "Order",
      placedOn: "Placed on",
      loading: "Loading order...",
      orderStatus: "Order Status",
      items: "Items",
      subtotal: "Subtotal",
      shipping: "Shipping",
      total: "Total",
      customerDetails: "Customer Details",
      name: "Name",
      phone: "Phone",
      email: "Email",
      city: "City",
      address: "Address",
      paymentMethod: "Payment Method",
      discount: "Discount",
      noDiscount: "No discount",
      newSubtotal: "New Subtotal",
      governorate: "Governorate",
      note: "Note",
      save: "Save",
      shippingMethod: "Shipping Method",

      // ✅ NEW
      extraFees: "Extra Fees",
      extraFeesText: "Fee name",
      extraFeesAmount: "Amount ($)",

      status: {
        pending: "Pending",
        processing: "Processing",
        packed: "Packed",
        shipped: "Shipped",
        delivered: "Delivered",
        cancelled: "Cancelled",
      },
    },
    AR: {
      back: "العودة إلى الطلبات",
      order: "طلب",
      placedOn: "تاريخ الطلب",
      loading: "جاري تحميل الطلب...",
      orderStatus: "حالة الطلب",
      items: "المنتجات",
      subtotal: "المجموع الفرعي",
      shipping: "الشحن",
      total: "الإجمالي",
      customerDetails: "معلومات العميل",
      name: "الاسم",
      phone: "الهاتف",
      email: "البريد الإلكتروني",
      city: "المدينة",
      address: "العنوان",
      paymentMethod: "طريقة الدفع",
      discount: "الخصم",
      noDiscount: "لا يوجد خصم",
      newSubtotal: "المجموع بعد الخصم",
      governorate: "المحافظة",
      note: "ملاحظة",
      save: "حفظ",
      shippingMethod: "طريقة الشحن",

      // ✅ NEW
      extraFees: "رسوم إضافية",
      extraFeesText: "اسم الرسوم",
      extraFeesAmount: "القيمة ($)",

      status: {
        pending: "قيد الانتظار",
        processing: "قيد المعالجة",
        packed: "تم التجهيز",
        shipped: "تم الشحن",
        delivered: "تم التسليم",
        cancelled: "ملغي",
      },
    },
  }[lang];

   const startEditCustomer = () => {
  setCustomerDraft({
    name: order.customer.name || "",
    phone: order.customer.phone || "",
    email: order.customer.email || "",
    governorate: order.customer.governorate || "",
    area: order.customer.area || "",
    street: order.customer.street || "",
    building: order.customer.building || "",
  });
  setEditingCustomer(true);
};

const saveCustomerDetails = async () => {
  try {
    await updateAdminOrderCustomer(order.id, customerDraft);

    const fresh = await getAdminOrderById(order.id);
    setOrder((prev) => ({
      ...fresh,
      _originalStatus: prev._originalStatus,
    }));

    setEditingCustomer(false);
    setShowCustomerToast(true);
    setTimeout(() => setShowCustomerToast(false), 3000);
  } catch (e) {
    alert(e.message);
  }
};


  useEffect(() => {
    let mounted = true;
    setLoading(true);

    getAdminOrderById(id)
      .then((data) => {
        if (mounted) {
          // ✅ ensure defaults exist (so inputs never break)
          setOrder({
            ...data,
            _originalStatus: data.status,
            extraFeeLabel: data?.extraFeeLabel || "",
            extraFeeAmount: Number(data?.extraFeeAmount || 0),
            adminNote: data?.adminNote || "",
          });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    return () => (mounted = false);
  }, [id]);

  const updateOrderStatus = async () => {
  const nextStatus = order.status;
  const prevStatus = order._originalStatus;

  let waWindow = null;

  // ✅ Open window ONLY for the two valid transitions
  const shouldSendConfirmWA =
    prevStatus === "pending" && nextStatus === "shipped";

  const shouldSendReviewWA =
    prevStatus === "shipped" && nextStatus === "delivered";

  if (shouldSendConfirmWA || shouldSendReviewWA) {
    waWindow = window.open("", "_blank");
  }

  try {
    await updateAdminOrderStatus(
      order.id,
      nextStatus,
      order.extraFeeLabel,
      order.extraFeeAmount,
      order.adminNote
    );

    const fresh = await getAdminOrderById(order.id);

    setOrder({
      ...fresh,
      _originalStatus: fresh.status, // ✅ update baseline
      extraFeeLabel: fresh?.extraFeeLabel || "",
      extraFeeAmount: Number(fresh?.extraFeeAmount || 0),
    });

    if (waWindow && fresh.customer?.phone) {
      let phone = fresh.customer.phone.replace(/\D/g, "");

      // ✅ Lebanon-safe fallback
      if (!phone.startsWith("961")) {
        phone = "961" + phone;
      }

      let msg = "";

      if (shouldSendConfirmWA) {
        msg = buildOrderConfirmationWhatsAppMessage(fresh);
      }

      if (shouldSendReviewWA) {
        msg = buildReviewRequestWhatsAppMessage(fresh);
      }

      if (msg) {
        waWindow.location.href = `https://wa.me/${phone}?text=${msg}`;
      } else {
        waWindow.close();
      }
    }

    setShowConfirm(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  } catch (e) {
    if (waWindow) waWindow.close();
    alert(e.message);
  }
};



  const formatDateTime = (d) => new Date(d).toLocaleString();

  const printOrder = (order) => {
    const w = window.open("", "_blank");

    const extraAmount = Number(order.extraFeeAmount || 0);
    const extraLabel = (order.extraFeeLabel || "").trim();

    w.document.write(`
      <html>
        <head>
          <title>Order ${order.orderNumber}</title>
          <style>
            body {
              font-family: Arial;
              padding: 20px;
              width: 148mm;
              margin: auto;
              text-align: center;
            }

            .section-title {
              border: 1px solid #000;
              padding: 6px;
              margin-top: 20px;
              font-weight: bold;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              text-align: center;
            }

            th, td {
              border: 1px solid #000;
              padding: 6px;
              font-size: 12px;
            }

            .box {
              border: 1px solid #000;
              border-radius: 10px;
              height: 60px;
              margin-top: 20px;
            }
          </style>
        </head>

        <body>

          <h2>Order #${order.orderNumber}</h2>

          <p><b>Date:</b> ${formatDateTime(order.created_at)}</p>
          <p><b>Total Items:</b> ${order.items.length}</p>

          <div class="section-title">Customer Details</div>
          <p><b>Name:</b> ${order.customer.name}</p>
          <p><b>Phone:</b> ${order.customer.phone}</p>
          <p><b>Email:</b> ${order.customer.email || ""}</p>
          <p><b>Governorate:</b> ${order.customer.governorate}</p>
<p><b>Area:</b> ${order.customer.area}</p>
<p><b>Street:</b> ${order.customer.street}</p>
<p><b>Building:</b> ${order.customer.building}</p>


          <div class="section-title">Products</div>
          <table>
            <tr>
              <th>Product ID</th>
              <th>Qty</th>
              <th>Details</th>
            </tr>
            ${order.items
              .map(
                (i) => `
                <tr>
                  <td>${i.public_id}</td>
                  <td>${i.quantity}</td>
                  <td>${i.color || ""} ${i.size || ""}</td>
                </tr>
              `
              )
              .join("")}
          </table>

          <div class="section-title">Payment Details</div>
          <p>Subtotal: $${order.subtotal.toFixed(2)}</p>
          <p>Discount: $${order.discount.toFixed(2)}</p>
          <p>Shipping: $${order.shippingCost.toFixed(2)}</p>
          ${
            extraAmount > 0
              ? `<p>Extra Fees: ${extraLabel ? `${extraLabel} ` : ""}$${extraAmount.toFixed(
                  2
                )}</p>`
              : ""
          }
          <p><b>Total: $${order.total.toFixed(2)}</b></p>

          <p><b>Note:</b> ${(order.orderNote || order.customer.note || "")}</p>

          <div class="section-title">Staff Notes</div>
          <div class="box">${order.adminNote || ""}</div>

          <script>
            window.print();
          </script>
        </body>
      </html>
    `);

    w.document.close();
  };

  if (loading || !order) {
    return (
      <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
        <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
        <div className="flex-1 lg:ml-64 transition-all">
          <AdminHeader setIsOpen={setIsOpen} />
          <div className="p-6 text-center text-xl text-gray-500">{TEXT.loading}</div>
        </div>
      </div>
    );
  }

  const extraAmount = Number(order.extraFeeAmount || 0);
  const extraLabel = (order.extraFeeLabel || "").trim();

  const totalItemsQty = order.items.reduce(
  (sum, i) => sum + Number(i.quantity || 0),
          0
      );


  return (
    <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="flex-1 lg:ml-64 transition-all">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 animate-fadeSlide">
          {/* Back */}
          <button
            onClick={() => navigate("/admin/orders")}
            className="flex items-center gap-2 text-cyan-700 hover:text-cyan-900 mb-6"
          >
            <FaArrowLeft /> {TEXT.back}
          </button>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-bold text-cyan-700 mb-3">
            {TEXT.order} #{order.orderNumber}
          </h1>

          <p className="text-gray-600 mb-6">
            {TEXT.placedOn} <b>{formatDateTime(order.created_at)}</b>
          </p>

          {/* Status */}
          <div className="bg-white p-4 rounded-xl shadow mb-6 border">
            <h2 className="text-lg font-semibold text-cyan-700 mb-3 flex items-center gap-2">
              <FaTruck /> {TEXT.orderStatus}
            </h2>

            <div className="flex gap-3 items-center flex-wrap">
              <select
                className="p-3 border rounded-xl shadow-sm"
                value={order.status}
                onChange={(e) => setOrder((prev) => ({ ...prev, status: e.target.value }))}
              >
                {["pending", "paid", "shipped", "delivered", "cancelled", "refunded"].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              <button
                onClick={() => setShowConfirm(true)}
                className="px-4 py-2 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700"
              >
                {TEXT.save}
              </button>

              <button
                onClick={() => printOrder(order)}
                className="px-4 py-2 bg-gray-700 text-white rounded-xl hover:bg-gray-800"
              >
                {lang === "EN" ? "Print" : "طباعة"}
              </button>
                
               {/* ✅ Admin Note */}
  <input
    type="text"
    value={order.adminNote || ""}
    onChange={(e) =>
      setOrder((prev) => ({
        ...prev,
        adminNote: e.target.value,
      }))
    }
    placeholder={lang === "EN" ? "Admin note (internal)" : "ملاحظة للإدارة"}
    className="
      min-w-[220px]
      px-3 py-2
      rounded-xl
      border border-gray-300
      text-sm
      bg-gray-50
      focus:ring-2 focus:ring-cyan-500
      focus:border-cyan-500
    "
  />  

            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Items */}
            <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow border">
              <h2 className="text-xl font-bold text-cyan-700 mb-4 flex items-center gap-2">
  <FaBox /> {TEXT.items}

  {/* ✅ Total quantity badge */}
  <span className="ml-2 px-3 py-1 rounded-full text-xs font-semibold
                   bg-cyan-50 text-cyan-700 border border-cyan-200">
    {totalItemsQty} {lang === "EN" ? "items" : "قطعة"}
  </span>
</h2>


              {order.items.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col sm:flex-row gap-4 p-3 border rounded-xl mb-3"
                >
                  <img src={item.image} className="w-20 h-20 rounded object-cover" alt="" />

                  <div className="flex-1">
  <p className="font-semibold flex items-center gap-2">
    {item.title_en}

    {item.public_id && (
      <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-600 border">
        {item.public_id}
      </span>
    )}
  </p>

  <p className="text-xs text-gray-400 mt-0.5">
    {lang === "EN" ? "Color" : "اللون"}: {item.color || "-"} •{" "}
    {lang === "EN" ? "Size" : "المقاس"}: {item.size || "-"}
  </p>

  <p className="text-sm text-gray-500 mt-1">
    {item.quantity} × ${item.price}
  </p>
</div>



                  <p className="font-bold">${(item.quantity * item.price).toFixed(2)}</p>
                </div>
              ))}

              <hr className="my-4" />

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>{TEXT.subtotal}</span>
                  <span>${order.subtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-sm text-gray-500">
                  <span>{TEXT.discount}</span>
                  <span>{order.discount > 0 ? `- $${order.discount.toFixed(2)}` : TEXT.noDiscount}</span>
                </div>

                <div className="flex justify-between font-medium">
                  <span>{TEXT.newSubtotal}</span>
                  <span>${order.newSubtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between">
                  <span>{TEXT.shipping}</span>
                  <span>${order.shippingCost.toFixed(2)}</span>
                </div>

                {/* ✅ NEW: extra fees row in summary */}
                {extraAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>
                      {TEXT.extraFees}
                      {extraLabel ? ` (${extraLabel})` : ""}
                    </span>
                    <span>${extraAmount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold bg-cyan-50 border border-cyan-200 px-4 py-2 rounded-xl text-cyan-800">
                  <span>{TEXT.total}</span>
                  <span>${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Customer */}
            <div className="bg-white p-6 rounded-xl shadow border">
              <div className="flex items-center justify-between mb-4">
  <h2 className="text-xl font-bold text-cyan-700 flex items-center gap-2">
    <FaUser /> {TEXT.customerDetails}
  </h2>

  {!editingCustomer ? (
    <button
      onClick={startEditCustomer}
      className="px-3 py-1.5 text-sm rounded-lg bg-gray-200 hover:bg-gray-300"
    >
      {lang === "EN" ? "Edit" : "تعديل"}
    </button>
  ) : (
    <button
      onClick={saveCustomerDetails}
      className="px-3 py-1.5 text-sm rounded-lg bg-cyan-600 text-white hover:bg-cyan-700"
    >
      {lang === "EN" ? "Save" : "حفظ"}
    </button>
  )}
</div>

{!editingCustomer ? (
  <>
    <p><b>{TEXT.name}:</b> {order.customer.name}</p>
    <p><b>{TEXT.phone}:</b> {order.customer.phone}</p>
    <p><b>{TEXT.email}:</b> {order.customer.email}</p>
    <p><b>{TEXT.governorate}:</b> {order.customer.governorate}</p>
    <p><b>{lang === "EN" ? "Area" : "المنطقة"}:</b> {order.customer.area}</p>
    <p><b>{lang === "EN" ? "Street" : "الشارع"}:</b> {order.customer.street}</p>
    <p><b>{lang === "EN" ? "Building" : "المبنى"}:</b> {order.customer.building}</p>

    {(order.customer.area || order.customer.street || order.customer.building) && (
      <div className="mt-3 p-3 rounded-lg bg-gray-50 border text-sm text-gray-700">
        <b>{lang === "EN" ? "Full Address:" : "العنوان الكامل:"}</b>{" "}
        {[order.customer.area, order.customer.street, order.customer.building]
          .filter(Boolean)
          .join(" - ")}
      </div>
    )}
  </>
) : (

  <div className="space-y-2">
  <input
    value={customerDraft.name}
    onChange={(e) => setCustomerDraft((p) => ({ ...p, name: e.target.value }))}
    placeholder={TEXT.name}
    className="w-full p-3 border rounded-xl text-sm"
  />

  <input
    value={customerDraft.phone}
    onChange={(e) => setCustomerDraft((p) => ({ ...p, phone: e.target.value }))}
    placeholder={TEXT.phone}
    className="w-full p-3 border rounded-xl text-sm"
  />

  <input
    value={customerDraft.email}
    onChange={(e) => setCustomerDraft((p) => ({ ...p, email: e.target.value }))}
    placeholder={TEXT.email}
    className="w-full p-3 border rounded-xl text-sm"
  />

  <input
    value={customerDraft.governorate}
    onChange={(e) =>
      setCustomerDraft((p) => ({ ...p, governorate: e.target.value }))
    }
    placeholder={TEXT.governorate}
    className="w-full p-3 border rounded-xl text-sm"
  />

  <input
    value={customerDraft.area}
    onChange={(e) => setCustomerDraft((p) => ({ ...p, area: e.target.value }))}
    placeholder={lang === "EN" ? "Area" : "المنطقة"}
    className="w-full p-3 border rounded-xl text-sm"
  />

  <input
    value={customerDraft.street}
    onChange={(e) => setCustomerDraft((p) => ({ ...p, street: e.target.value }))}
    placeholder={lang === "EN" ? "Street" : "الشارع"}
    className="w-full p-3 border rounded-xl text-sm"
  />

  <input
    value={customerDraft.building}
    onChange={(e) =>
      setCustomerDraft((p) => ({ ...p, building: e.target.value }))
    }
    placeholder={lang === "EN" ? "Building" : "المبنى"}
    className="w-full p-3 border rounded-xl text-sm"
  />
</div>
)}
       

{order.orderNote && (
  <div className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
    <p className="text-sm text-yellow-900">
      <b>{lang === "EN" ? "Order Note:" : "ملاحظة الطلب:"}</b> {order.orderNote}
    </p>
  </div>
)}


              <div className="mt-5">
                <h3 className="font-bold text-cyan-700 flex items-center gap-2">
                  <FaMoneyBill /> {TEXT.paymentMethod}
                </h3>
                <p>{order.payment}</p>

                <div className="mt-2">
                  <span className="text-sm text-gray-500 block">{TEXT.shippingMethod}</span>

                  <span
                    className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold
                      ${
                        order.shippingMethod === "pickup"
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700"
                      }
                    `}
                  >
                    {order.shippingMethod === "pickup"
                      ? lang === "EN"
                        ? "Pickup from store"
                        : "استلام من المتجر"
                      : lang === "EN"
                      ? "Delivery"
                      : "توصيل"}
                  </span>
                </div>

                
              </div>

              {/* ✅ NEW: Extra Fees section (admin editable) */}
              <div className="mt-6 border-t pt-4">
                <h3 className="font-bold text-cyan-700 mb-3">{TEXT.extraFees}</h3>

                <div className="space-y-2">
                  <input
                    value={order.extraFeeLabel || ""}
                    onChange={(e) =>
                      setOrder((prev) => ({
                        ...prev,
                        extraFeeLabel: e.target.value,
                      }))
                    }
                    placeholder={TEXT.extraFeesText}
                    className="w-full p-3 border rounded-xl shadow-sm text-sm"
                  />

                  <input
                    type="number"
                    step="0.01"
                    value={order.extraFeeAmount ?? 0}
                    onChange={(e) =>
                      setOrder((prev) => ({
                        ...prev,
                        extraFeeAmount: e.target.value,
                      }))
                    }
                    placeholder={TEXT.extraFeesAmount}
                    className="w-full p-3 border rounded-xl shadow-sm text-sm"
                  />

                  <p className="text-xs text-gray-500">
                    {lang === "EN"
                      ? "Tip: Click Save to apply extra fees to the final total."
                      : "ملاحظة: اضغط حفظ لتطبيق الرسوم الإضافية على الإجمالي."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {showToast && (
            <div className="fixed bottom-6 right-6 z-[9999] animate-fadeSlide">
              <div className="bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3">
                <span className="text-lg">✅</span>
                <span className="text-sm font-medium">
                  {lang === "EN"
                    ? "Order updated successfully"
                    : "تم تحديث الطلب بنجاح"}
                </span>
              </div>
            </div>
          )}

{showCustomerToast && (
  <div className="fixed bottom-6 left-6 z-[9999] animate-fadeSlide">
    <div className="bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3">
      <span className="text-lg">✅</span>
      <span className="text-sm font-medium">
        {lang === "EN"
          ? "Customer details updated successfully"
          : "تم تحديث بيانات العميل بنجاح"}
      </span>
    </div>
  </div>
)}

          {showConfirm && (
            <div className="fixed inset-0 bg-black/40 z-[9998] flex items-center justify-center">
              <div className="bg-white w-80 rounded-2xl p-6 shadow-xl animate-fadeSlide">
                <h3 className="text-lg font-bold text-cyan-700 mb-2">
                  {lang === "EN" ? "Confirm Update" : "تأكيد التحديث"}
                </h3>

                <p className="text-sm text-gray-600 mb-6">
                  {lang === "EN"
                    ? "Are you sure you want to update the order?"
                    : "هل أنت متأكد من تحديث الطلب؟"}
                </p>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300"
                  >
                    {lang === "EN" ? "Cancel" : "إلغاء"}
                  </button>

                  <button
                    onClick={updateOrderStatus}
                    className="px-4 py-2 rounded-xl bg-cyan-600 text-white hover:bg-cyan-700"
                  >
                    {lang === "EN" ? "Confirm" : "تأكيد"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminOrderDetails;
